import React, { useState } from "react";
import {
    Button,
    Link,
    Typography,
    Stack,
    Grid,
    TextField,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    CircularProgress
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS } from "date-fns/locale";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { MobileBookingsTheme } from "../../styles/MobileBookingsTheme.js";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { DialogTheme } from "../../styles/DialogTheme.js";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useBookings } from "./useBookings.js";
import { useProviderSchedule } from "./useProviderSchedule.js";
import {
    dateRangeAtom,
    filteredBookingsAtom,
    showWaitlistAtom,
    openedInfoWindowTypeAtom
} from "./analysisAtoms.js";
import { useAtom } from "jotai";
import { useWaitList } from "./useWaitList.js";
import MapMarker from "./MapMarker.js";
import TravelRegionPolygon from "./TravelRegionPolygon.js";
import ProviderFilter from "./providerFilter.js";
import { AddressLookupControl } from "./AddressLookupControl.js";
import { TravelZoneSwitchControl } from "./TravelZonesSwitchControl.js";
import AddressMarker from "./AddressMarker.js";
import { WaitlistSwitchControl } from "./WaitlistSwitchControl.js";
import { colors, INFO_WINDOW_NONE } from "../../utils/Constants.js";
import { ExpiredTravelZonesSwitchControl } from "./ExpiredTravelZonesSwitchControl.js";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

const MapHeader = () => {
    const [dateRange, setDateRange] = useAtom(dateRangeAtom);
    const [localDateRange, setLocalDateRange] = useState({ ...dateRange });
    const theme = useTheme();
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const [openDateFilter, setOpenDateFilter] = useState(false);
    const [dateError, setDateError] = useState("");
    const handleApply = () => {
        setDateRange({ ...localDateRange });
        setOpenDateFilter(false);
    };

    return (
        <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="18px"
            sx={{ marginBottom: "10px" }}
        >
            <Link align="center" onClick={() => setOpenDateFilter(true)}>
                <ThemeProvider theme={MobileBookingsTheme(theme)}>
                    <Typography
                        variant="link"
                        color="primary"
                        sx={{
                            "&:hover": { textDecoration: "none" }
                        }}
                    >
                        <b>Date range</b>
                    </Typography>
                </ThemeProvider>
            </Link>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
            >
                <TravelZoneSwitchControl />
                <ExpiredTravelZonesSwitchControl />
                <WaitlistSwitchControl />
            </Stack>
            <ProviderFilter />

            {openDateFilter && (
                <ThemeProvider theme={DialogTheme(theme)}>
                    <Dialog
                        open={openDateFilter}
                        onClose={() => {
                            setOpenDateFilter(false);
                            setOpenStartDate(false);
                            setOpenEndDate(false);
                        }}
                    >
                        <DialogTitle sx={{ paddingBottom: "15px" }}>
                            Bookings date range
                        </DialogTitle>
                        <DialogContent>
                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={{
                                    ...enUS,
                                    options: {
                                        ...enUS.options,
                                        weekStartsOn: 1
                                    }
                                }}
                            >
                                <Grid
                                    container
                                    spacing={2.25}
                                    sx={{ marginTop: "0px" }}
                                >
                                    <Grid item xs={6} sm={6}>
                                        <DatePicker
                                            autoFocus
                                            id="start-date"
                                            name="startDate"
                                            size="small"
                                            label="Start date"
                                            value={localDateRange.start}
                                            fullWidth
                                            inputFormat="MMM d, yyyy"
                                            open={openStartDate}
                                            onOpen={() =>
                                                setOpenStartDate(true)
                                            }
                                            onClose={() =>
                                                setOpenStartDate(false)
                                            }
                                            onChange={(newValue) => {
                                                setLocalDateRange({
                                                    start: newValue,
                                                    end: localDateRange.end
                                                });
                                                if (
                                                    newValue &&
                                                    localDateRange.end &&
                                                    newValue >
                                                        localDateRange.end
                                                ) {
                                                    setDateError(
                                                        "Start date cannot be after end date"
                                                    );
                                                } else {
                                                    setDateError("");
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    error={dateError}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        readOnly: true
                                                    }}
                                                    onClick={() =>
                                                        setOpenStartDate(true)
                                                    }
                                                    sx={{
                                                        "& .MuiInputAdornment-root":
                                                            {
                                                                display: "none"
                                                            },
                                                        "& fieldset": {
                                                            "& legend": {
                                                                width: "54px"
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={6}>
                                        <DatePicker
                                            id="end-date"
                                            name="endDate"
                                            size="small"
                                            label="End date"
                                            value={localDateRange.end}
                                            fullWidth
                                            inputFormat="MMM d, yyyy"
                                            open={openEndDate}
                                            onOpen={() => setOpenEndDate(true)}
                                            onClose={() =>
                                                setOpenEndDate(false)
                                            }
                                            onChange={(newValue) => {
                                                setLocalDateRange({
                                                    start: localDateRange.start,
                                                    end: newValue
                                                });

                                                if (
                                                    localDateRange.start &&
                                                    newValue &&
                                                    localDateRange.start >
                                                        newValue
                                                ) {
                                                    setDateError(
                                                        "End date cannot be before start date."
                                                    );
                                                } else {
                                                    setDateError("");
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        readOnly: true
                                                    }}
                                                    onClick={() =>
                                                        setOpenEndDate(true)
                                                    }
                                                    sx={{
                                                        "& .MuiInputAdornment-root":
                                                            {
                                                                display: "none"
                                                            },
                                                        "& fieldset": {
                                                            "& legend": {
                                                                width: "50px"
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    {dateError && (
                                        <Grid
                                            variant="errorTextContainer"
                                            item
                                            xs={12}
                                            sm={12}
                                        >
                                            <Typography variant="errorText">
                                                {dateError}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </LocalizationProvider>
                        </DialogContent>
                        <DialogActions>
                            <Grid
                                container
                                item
                                gap={"18px"}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end"
                                }}
                            >
                                <Button
                                    variant="cancelButton"
                                    onClick={() => {
                                        setOpenDateFilter(false);
                                        setOpenStartDate(false);
                                        setOpenEndDate(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleApply}
                                    disabled={!!dateError}
                                >
                                    Apply
                                </Button>
                            </Grid>
                        </DialogActions>
                    </Dialog>
                </ThemeProvider>
            )}
        </Stack>
    );
};
const MAP_CENTER = { lat: 45.4215, lng: -75.6972 };
const MapComponent = () => {
    const { isLoading: loadingProviders } = useProviderSchedule();
    const { isLoading: loadingBookings } = useBookings();
    const [showWaitList] = useAtom(showWaitlistAtom);
    const [, setOpenedInfoWindowType] = useAtom(openedInfoWindowTypeAtom);
    return (
        <>
            {!loadingProviders && !loadingBookings && <MapHeader />}

            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <Map
                    id="data-analysis"
                    mapId="787690420a32c79f"
                    style={{
                        width: "100%",
                        height: "700px"
                    }}
                    streetViewControl={false}
                    defaultCenter={MAP_CENTER}
                    defaultZoom={4}
                    mapTypeControl={false}
                    onClick={() => setOpenedInfoWindowType(INFO_WINDOW_NONE)}
                >
                    {loadingProviders || loadingBookings ? (
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                padding: "10px"
                            }}
                        >
                            <CircularProgress size={50} />
                        </div>
                    ) : (
                        <>
                            <BookingMapMarkers />
                            <TravelRegionPolygon />
                            {showWaitList && <WaitlistMapMarkers />}
                            <AddressLookupControl />
                            <AddressMarker />
                        </>
                    )}
                </Map>
            </APIProvider>
        </>
    );
};

const BookingMapMarkers = () => {
    const [bookings] = useAtom(filteredBookingsAtom);
    return bookings ? (
        <MapMarker
            data={bookings}
            dataKey="id"
            getPosition={(booking) => booking.coords}
            getPinColor={() => colors.MB_INFO_COLOR_MAIN}
            infoWindowHeaderText="Booking"
            infoWindowContent={(booking) => (
                <>
                    {booking.provider && (
                        <Typography variant="body2" sx={{ margin: "4px" }}>
                            <strong>Provider: </strong>{" "}
                            {booking.provider.firstname}{" "}
                            {booking.provider.lastname}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ margin: "4px" }}>
                        <strong>Service: </strong> {booking.desc}
                    </Typography>
                    {booking.client?.user && (
                        <Typography variant="body2" sx={{ margin: "4px" }}>
                            <strong>Client: </strong>{" "}
                            {booking.client.user.firstname}{" "}
                            {booking.client.user.lastname}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ margin: "4px" }}>
                        <strong>Address: </strong>
                        {booking.location}
                    </Typography>
                </>
            )}
        />
    ) : (
        <></>
    );
};

const WaitlistMapMarkers = () => {
    const { data: waitlist } = useWaitList();
    const [showWaitList] = useAtom(showWaitlistAtom);
    return waitlist && waitlist.length ? (
        <MapMarker
            data={showWaitList ? waitlist : []}
            dataKey="prospectId"
            getPosition={(prospect) => ({
                lat: prospect.latitude,
                lng: prospect.longitude
            })}
            getPinColor={() => colors.MB_MAP_WAITLIST_PIN}
            infoWindowHeaderText="Prospect"
            infoWindowContent={(prospect) => (
                <>
                    <Typography variant="body2" sx={{ margin: "4px" }}>
                        <strong>Created on: </strong>{" "}
                        {prospect.formattedCreatedAt}
                    </Typography>
                    <Typography variant="body2" sx={{ margin: "4px" }}>
                        <strong>Client: </strong> {prospect.firstname}{" "}
                        {prospect.lastname}
                    </Typography>
                    <Typography variant="body2" sx={{ margin: "4px" }}>
                        <strong>Address: </strong> {prospect.addressoneline}
                    </Typography>
                    {prospect.serviceNames?.length && (
                        <Typography variant="body2" sx={{ margin: "4px" }}>
                            <strong>Services: </strong>{" "}
                            {prospect.serviceNames.join(",")}
                        </Typography>
                    )}
                    {prospect.insideTravelZoneMessages?.length ? (
                        prospect.insideTravelZoneMessages.map((msg) => (
                            <Typography variant="body2">{msg}</Typography>
                        ))
                    ) : (
                        <></>
                    )}
                    {prospect.nearestProviderMessage ? (
                        <Typography variant="body2">
                            {prospect.nearestProviderMessage}
                        </Typography>
                    ) : (
                        <></>
                    )}
                </>
            )}
        />
    ) : (
        <></>
    );
};
export default MapComponent;
